import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Ticket, Boxes, AlertCircle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { servicenowCmdbApi, type IncidentContext, type IncidentPayload } from '@/features/servicenow-cmdb/api'

export interface IncidentPrefill {
    shortDescription: string
    description: string
    urgency: string // '1' | '2' | '3'
    impact: string  // '1' | '2' | '3'
    correlationId: string
}

interface RaiseIncidentDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    objectId: string
    context: IncidentContext | null
    prefill: IncidentPrefill
}

const URGENCY_IMPACT = [
    { value: '1', labelKey: 'high', label: '1 - High' },
    { value: '2', labelKey: 'medium', label: '2 - Medium' },
    { value: '3', labelKey: 'low', label: '3 - Low' },
]

// Common ServiceNow incident categories. The exact choice list is instance-specific;
// these cover the defaults plus Security (sensible for a scan-driven incident).
const CATEGORIES = ['Security', 'Inquiry / Help', 'Software', 'Hardware', 'Network', 'Database']

export function RaiseIncidentDialog({ open, onOpenChange, objectId, context, prefill }: RaiseIncidentDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()

    // Prefilled-from-scan fields
    const [shortDescription, setShortDescription] = useState('')
    const [description, setDescription] = useState('')
    const [urgency, setUrgency] = useState('2')
    const [impact, setImpact] = useState('2')
    const [contactType, setContactType] = useState('Integration')
    const [correlationId, setCorrelationId] = useState('')
    // Configuration Item — required; pre-filled from the CMDB sync when linked
    const [cmdbCi, setCmdbCi] = useState('')
    // Mandatory; Caller + Assignment group pre-fill from the defaults configured in settings
    const [callerId, setCallerId] = useState('')
    const [assignmentGroup, setAssignmentGroup] = useState('')
    const [category, setCategory] = useState('')
    const [workNotes, setWorkNotes] = useState('')

    // (Re)seed the form from the scan each time the dialog opens.
    useEffect(() => {
        if (!open) return
        setShortDescription(prefill.shortDescription)
        setDescription(prefill.description)
        setUrgency(prefill.urgency || '2')
        setImpact(prefill.impact || '2')
        setContactType('Integration')
        setCorrelationId(prefill.correlationId)
        setCmdbCi(context?.ci?.sysId || '')
        setCallerId(context?.defaultCaller || '')
        setAssignmentGroup(context?.defaultAssignmentGroup || '')
        setCategory('')
        setWorkNotes('')
    }, [open, prefill, context])

    const ci = context?.ci || null

    const mutation = useMutation({
        mutationFn: () => {
            const payload: IncidentPayload = {
                short_description: shortDescription.trim(),
                description: description.trim() || undefined,
                cmdb_ci: cmdbCi.trim() || undefined,
                caller_id: callerId.trim() || undefined,
                urgency,
                impact,
                category: category.trim() || undefined,
                assignment_group: assignmentGroup.trim() || undefined,
                contact_type: contactType.trim() || undefined,
                correlation_id: correlationId.trim() || undefined,
                work_notes: workNotes.trim() || undefined,
            }
            return servicenowCmdbApi.createIncident(payload).then(r => r.data.data)
        },
        onSuccess: (res) => {
            toast({
                title: t('objects.incident.raised', 'Incident raised'),
                description: res.number
                    ? t('objects.incident.raisedDetail', { defaultValue: 'ServiceNow incident {{number}} created.', number: res.number })
                    : t('objects.incident.raisedNoNumber', 'ServiceNow incident created.'),
            })
            onOpenChange(false)
        },
        onError: (err: any) => {
            toast({
                title: t('objects.incident.failed', 'Could not raise incident'),
                description: err?.response?.data?.message || err?.message || 'Unknown error',
                variant: 'destructive',
            })
        },
    })

    // A field "needs populating" → it is mandatory. Send is blocked until all are filled.
    const requiredValues: Record<string, string> = {
        short_description: shortDescription,
        cmdb_ci: cmdbCi,
        caller_id: callerId,
        assignment_group: assignmentGroup,
        category,
        urgency,
        impact,
    }
    const missingCount = Object.values(requiredValues).filter(v => !v || !v.trim()).length
    const canSend = missingCount === 0 && !mutation.isPending

    const req = <span className="text-red-500" aria-hidden>*</span>

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Ticket className="h-5 w-5 text-blue-600" />
                        {t('objects.incident.title', 'Raise ServiceNow Incident')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('objects.incident.subtitle', 'Fields marked * must be completed before you can send. Pre-filled values came from the scan and can be edited.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-1">
                    {/* Configuration Item (cmdb_ci) — required. Pre-filled from the CMDB sync when
                        the object is linked; otherwise the operator enters the CI sys_id. */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-ci" className="flex items-center gap-2">
                            <Boxes className="h-3.5 w-3.5" />
                            {t('objects.incident.ci', 'Configuration Item (cmdb_ci)')} {req}
                        </Label>
                        <Input id="inc-ci" value={cmdbCi} onChange={e => setCmdbCi(e.target.value)}
                            placeholder={t('objects.incident.ciPlaceholder', 'ServiceNow CI sys_id')} />
                        {ci ? (
                            <p className="text-[11px] text-muted-foreground">
                                {t('objects.incident.ciSynced', 'Linked CI from CMDB sync:')} <span className="font-medium">{ci.ciName || objectId}</span>{ci.ciClass ? ` · ${ci.ciClass}` : ''}
                            </p>
                        ) : (
                            <p className="flex items-start gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                                <AlertCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                                {t('objects.incident.noCi', 'No synced CI found for this object — enter the ServiceNow CI sys_id, or sync the object from the CMDB to auto-fill it.')}
                            </p>
                        )}
                    </div>

                    {/* Short description */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-short">{t('objects.incident.shortDescription', 'Short description')} {req}</Label>
                        <Input id="inc-short" value={shortDescription} onChange={e => setShortDescription(e.target.value)} maxLength={160} />
                    </div>

                    {/* Description */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-desc">{t('objects.incident.description', 'Description')}</Label>
                        <Textarea id="inc-desc" rows={4} value={description} onChange={e => setDescription(e.target.value)} />
                    </div>

                    {/* Urgency + Impact */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label>{t('objects.incident.urgency', 'Urgency')} {req}</Label>
                            <Select value={urgency} onValueChange={setUrgency}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {URGENCY_IMPACT.map(o => (
                                        <SelectItem key={o.value} value={o.value}>{t('objects.incident.prio_' + o.labelKey, o.label)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1.5">
                            <Label>{t('objects.incident.impact', 'Impact')} {req}</Label>
                            <Select value={impact} onValueChange={setImpact}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {URGENCY_IMPACT.map(o => (
                                        <SelectItem key={o.value} value={o.value}>{t('objects.incident.prio_' + o.labelKey, o.label)}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Caller — mandatory, cannot be derived */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-caller">{t('objects.incident.caller', 'Caller')} {req}</Label>
                        <Input id="inc-caller" value={callerId} onChange={e => setCallerId(e.target.value)}
                            placeholder={t('objects.incident.callerPlaceholder', 'ServiceNow user (sys_id or user name)')} />
                    </div>

                    {/* Assignment group — mandatory */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-group">{t('objects.incident.assignmentGroup', 'Assignment group')} {req}</Label>
                        <Input id="inc-group" value={assignmentGroup} onChange={e => setAssignmentGroup(e.target.value)}
                            placeholder={t('objects.incident.groupPlaceholder', 'Group (sys_id or name)')} />
                    </div>

                    {/* Category — mandatory */}
                    <div className="space-y-1.5">
                        <Label>{t('objects.incident.category', 'Category')} {req}</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger><SelectValue placeholder={t('objects.incident.categoryPlaceholder', 'Select a category')} /></SelectTrigger>
                            <SelectContent>
                                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Contact type + Correlation ID (pre-filled, editable) */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="inc-contact">{t('objects.incident.contactType', 'Contact type')}</Label>
                            <Input id="inc-contact" value={contactType} onChange={e => setContactType(e.target.value)} />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="inc-corr">{t('objects.incident.correlationId', 'Correlation ID')}</Label>
                            <Input id="inc-corr" value={correlationId} onChange={e => setCorrelationId(e.target.value)} />
                        </div>
                    </div>

                    {/* Work notes (optional) */}
                    <div className="space-y-1.5">
                        <Label htmlFor="inc-notes">{t('objects.incident.workNotes', 'Work notes (optional)')}</Label>
                        <Textarea id="inc-notes" rows={2} value={workNotes} onChange={e => setWorkNotes(e.target.value)} />
                    </div>
                </div>

                <DialogFooter className="gap-2 sm:gap-2">
                    {missingCount > 0 && (
                        <span className="text-xs text-muted-foreground mr-auto self-center">
                            {t('objects.incident.missing', { defaultValue: '{{count}} required field(s) to complete', count: missingCount })}
                        </span>
                    )}
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={() => mutation.mutate()} disabled={!canSend}>
                        {mutation.isPending ? t('objects.incident.sending', 'Sending…') : t('objects.incident.send', 'Send to ServiceNow')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
